import { Box, Container } from '@mui/material';
import { useLocalStorage } from '@uidotdev/usehooks';
import * as date from 'date-fns';
import { useState } from 'react';
import { RouteObject } from 'react-router-dom';
import useSWR from 'swr';
import { PageSpinner, Video } from '~/components';
import { ChartCard } from '~/components/ChartCard';
import { PageHeader } from '~/components/PageHeader';
import { useFarm } from '~/hooks/useFarm';
import { applyStandardDeviation1SD,applyStandardDeviationpoint5SD } from '~/utils/stddev';
// --- CSV imports (past/baseline data) ---
import birdCountAvgCsvRaw from './WallPastData/DAY_MEAN_BIRDCOUNT.csv?raw';
import birdCountSdCsvRaw from './WallPastData/DAY_SD1_BIRDCOUNT.csv?raw';
import wallDensityAvgCsvRaw from './WallPastData/DAY_MEAN_WALLDENSITY.csv?raw';
import wallDensitySdCsvRaw from './WallPastData/DAY_SD1_WALLDENSITY.csv?raw';


export const wallRoute: RouteObject = {
  path: '/wall',
  element: <Wall />,
}

const defaultFrom = date.subWeeks(new Date(), 6)
const defaultTo = date.addDays(new Date(), 1)

function Wall() {
  const { farm, isLoading: isLoadingFarm, accessStartDate } = useFarm()

  const [from, setFrom] = useLocalStorage('settings.from', date.formatISO(defaultFrom, { representation: 'date' }))
  const [to, setTo] = useLocalStorage('settings.to', date.formatISO(defaultTo, { representation: 'date' }))
  const [params, setParams] = useState({ from, to })
  
  // Convert access start date to Date object for date picker minDate
  const minDate = accessStartDate ? date.parseISO(accessStartDate) : null

  const { data: feederData = [], isLoading } = useSWR(
    farm?.id ? `/farms/${farm.id}/insights/wall?dateFrom=${params.from}&dateTo=${params.to}` : null)

  const { data: media } = useSWR(
    farm?.id ? `/farms/${farm.id}/media/wall/${params.to.replace(/-/g, '')}` : null)

  
  const parseNumberLines = (text: string): number[] =>
  text
    .trim()
    .split(/\r?\n/)
    .map((line) => Number(line.trim()))
    .filter((n) => !Number.isNaN(n));

function computeCycleLength(birdAges: number[]): number {
  let maxBeforeReset = 0;

  for (let i = 0; i < birdAges.length; i++) {
    const age = birdAges[i];
    if (age === 1 && i !== 0) break;
    if (age > maxBeforeReset) maxBeforeReset = age;
  }

  return maxBeforeReset;
}

// Past data from CSV 
const birdCountPastAvg = parseNumberLines(birdCountAvgCsvRaw);
const birdCountPastSd = parseNumberLines(birdCountSdCsvRaw);

const wallDensityPastAvg = parseNumberLines(wallDensityAvgCsvRaw);
const wallDensityPastSd = parseNumberLines(wallDensitySdCsvRaw);

// cycle based on live BirdAgelive 
const ages = feederData.map((d: any) => Number(d.age ?? 0));
const CYCLE = computeCycleLength(ages);

// avoid modulo by 0
const cycleSafe = CYCLE > 0 ? CYCLE : Math.max(birdCountPastAvg.length, wallDensityPastAvg.length, 1);

const isMoyParkFarm = farm?.id === "experiment-moypark";




const AGE_INVALID = Number.MAX_SAFE_INTEGER;
const birdCount = feederData.map((d: any) => {
  const birdage = Number(d.birdage ?? d.birdAge ?? d.age);

  const wrappedAge =
    Number.isFinite(birdage) && birdage > 0 && isMoyParkFarm
      ? ((birdage - 1) % cycleSafe) + 1
      : AGE_INVALID;

  const idx = wrappedAge - 1;
//  if wrappedAge exceeds CSV range,do not render any past data
   const outOfRange = idx < 0 || idx >= birdCountPastAvg.length;
    const pastavg = Math.max(0, (birdCountPastAvg[idx] ?? 0));
  const realstdDev = birdCountPastSd[idx] ?? 0;   // past sd 

  
  if (outOfRange) {
    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg: null,
      avg: d.birdCountAvg,
      age: wrappedAge,
      stddev: null,
      stddevp5: null
    };
  }

 
  return {
    datetime: date.addHours(date.parseISO(d.date), d.hour),
    date: d.date,
    hour: d.hour,
    pastavg,
    avg:d.birdCountAvg??0,
    age:wrappedAge,
    stddev: applyStandardDeviation1SD(pastavg, realstdDev),
    stddevp5: applyStandardDeviationpoint5SD(pastavg, realstdDev),
    
  };
});


  const birdDensity = feederData.map((d: any) => {
  const birdage = Number(d.birdage ?? d.birdAge ?? d.age);

  const wrappedAge =
    Number.isFinite(birdage) && birdage > 0 && isMoyParkFarm
      ? ((birdage - 1) % cycleSafe) + 1
      : AGE_INVALID;

  const idx = wrappedAge - 1;
  //  if wrappedAge exceeds CSV range,do not render any past data
   const outOfRange = idx < 0 || idx >= wallDensityPastAvg.length;
   const pastavg = Math.max(0, (wallDensityPastAvg[idx] ?? 0));
  const realstdDev = wallDensityPastSd[idx] ?? 0; // past sd 

  
  if (outOfRange) {
    return {
      datetime: date.addHours(date.parseISO(d.date), d.hour),
      date: d.date,
      hour: d.hour,
      pastavg: null,
      avg: d.birdDensityAvg,
      age: wrappedAge,
      stddev: null,
      stddevp5: null
    };
  }

  
  return {
    datetime: date.addHours(date.parseISO(d.date), d.hour),
    date: d.date,
    hour: d.hour,
    pastavg,
    avg:d.birdDensityAvg ??0,
    age:wrappedAge,
    stddev: applyStandardDeviation1SD(pastavg, realstdDev),
    stddevp5: applyStandardDeviationpoint5SD(pastavg, realstdDev),
    
  };
});

 
  const handleApply = () => {
    setParams({ from, to })
  }

  return (
    <Container maxWidth={false} sx={{pb: '24px'}}>
      <PageHeader title="Wall" from={from} to={to} setFrom={setFrom} setTo={setTo} handleApply={handleApply} minDate={minDate} />
      {(isLoadingFarm || isLoading) && <PageSpinner />}
      {!isLoadingFarm && !isLoading && (
        <Box display={"grid"} gridTemplateColumns={"repeat(auto-fill, minmax(500px, 700px))"} gap={2}>
          {media?.url && <Video height='375px' width='100%' src={media.url} />}
          <ChartCard title="Bird count" data={birdCount} />
          <ChartCard title="Bird Density" data={birdDensity} />
        </Box>
      )}
    </Container>
  )
}
